import { doorCopy, doorRules, doorTypeIds } from '@planner/config';
import { sanitizeInput } from '../validation.js';
import { deriveManufacturedSize, evaluateLockFit, securityLockPositions, validateDoorForCommit, } from './derive.js';
/** Backend `updateOrderSchema` allows more; the door line stays inside 20. */
export const DOOR_OPTION_LIMIT = 20;
export const DOOR_DESCRIPTION_LIMIT = 200;
const MM_OPTION_KEYS = new Set([
    'mfgWidth',
    'mfgHeight',
    'height',
    'heightRight',
    'width',
    'widthTop',
    'widthMiddle',
    'widthBottom',
    'lockHeight',
    'lockTop',
    'lockBottom',
]);
const OPTION_FIELDS = [
    { key: 'doorName', label: 'Door name' },
    { key: 'doorType', label: 'Door type' },
    { key: 'measurementMode', label: 'Measurement' },
    { key: 'mfgWidth', label: 'Manufactured width' },
    { key: 'mfgHeight', label: 'Manufactured height' },
    { key: 'widthTop', label: 'Width (top)' },
    { key: 'widthMiddle', label: 'Width (middle)' },
    { key: 'widthBottom', label: 'Width (bottom)' },
    { key: 'width', label: 'Width' },
    { key: 'height', label: 'Height' },
    { key: 'heightRight', label: 'Height (right)' },
    { key: 'outOfSquareAck', label: 'Out of square acknowledged' },
    { key: 'lockSide', label: 'Handle side' },
    { key: 'lockHeight', label: 'Handle height' },
    { key: 'lockTop', label: 'Top lock' },
    { key: 'lockBottom', label: 'Bottom lock' },
    { key: 'colourCode', label: 'Colour code' },
    { key: 'colourName', label: 'Colour' },
    { key: 'bunningsItem', label: 'Bunnings item' },
    { key: 'catalogueSyncedAt', label: 'Catalogue synced' },
];
function measurementEntries(measurements) {
    if (measurements.mode === 'diy') {
        return [
            ['height', String(measurements.height)],
            ['heightRight', String(measurements.heightRight)],
            ['widthTop', String(measurements.widthTop)],
            ['widthMiddle', String(measurements.widthMiddle)],
            ['widthBottom', String(measurements.widthBottom)],
        ];
    }
    return [
        ['width', String(measurements.width)],
        ['height', String(measurements.height)],
    ];
}
function optionEntries(door, size) {
    const entries = [
        ['doorName', door.doorName],
        ['doorType', door.doorType],
        ['measurementMode', door.measurements.mode],
        ['mfgWidth', String(size.width)],
        ['mfgHeight', String(size.height)],
        ...measurementEntries(door.measurements),
        ['outOfSquareAck', door.outOfSquareAcknowledged ? 'true' : 'false'],
        ['lockSide', door.lockSide],
        ['lockHeight', String(door.lockHeight)],
    ];
    if (door.doorType === 'security') {
        const locks = securityLockPositions(door.lockHeight);
        if (locks) {
            entries.push(['lockTop', String(locks.top)]);
            if (locks.bottom !== undefined) {
                entries.push(['lockBottom', String(locks.bottom)]);
            }
        }
    }
    entries.push(['colourCode', door.colourCode], ['colourName', door.snapshot.colourName], ['bunningsItem', door.snapshot.bunningsItemNumber], ['catalogueSyncedAt', door.snapshot.catalogueSyncedAt]);
    return entries.map(([key, value]) => [
        key,
        sanitizeInput(value.slice(0, DOOR_DESCRIPTION_LIMIT), DOOR_DESCRIPTION_LIMIT),
    ]);
}
/** One order line. `unitPrice` is the snapshot's GST-inclusive price. */
export function doorToOrderItem(door) {
    const validation = validateDoorForCommit(door);
    if (!validation.valid) {
        throw new Error(validation.message);
    }
    const entries = optionEntries(door, validation.size);
    if (entries.length > DOOR_OPTION_LIMIT) {
        throw new Error(`Door options exceed ${DOOR_OPTION_LIMIT} entries (${entries.length})`);
    }
    return {
        sku: sanitizeInput(door.snapshot.sku, door.snapshot.sku.length),
        description: sanitizeInput(door.snapshot.description.slice(0, DOOR_DESCRIPTION_LIMIT), DOOR_DESCRIPTION_LIMIT),
        quantity: door.quantity,
        unitPrice: door.snapshot.unitPrice,
        options: Object.fromEntries(entries),
    };
}
export function encodeDoorsDesign(input) {
    for (const door of input.doors) {
        const validation = validateDoorForCommit(door);
        if (!validation.valid) {
            throw new Error(validation.message);
        }
    }
    return {
        schema: 'doors',
        version: doorRules.designVersion,
        doors: [...input.doors],
        ...(input.termsAcceptedAt !== undefined ? { termsAcceptedAt: input.termsAcceptedAt } : {}),
        ...(input.clonedFrom !== undefined ? { clonedFrom: input.clonedFrom } : {}),
        ...(input.appVersion !== undefined ? { appVersion: input.appVersion } : {}),
    };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Compare persisted configurations independently of object key order and design metadata. */
export function doorsOrderFingerprint(design, items) {
    const configuration = isRecord(design) && design.schema === 'doors'
        ? { schema: design.schema, version: design.version, doors: design.doors }
        : design;
    return JSON.stringify([configuration, items], (_key, value) => isRecord(value)
        ? Object.fromEntries(Object.keys(value)
            .sort()
            .map((key) => [key, value[key]]))
        : value);
}
function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
function isDoorType(value) {
    return typeof value === 'string' && doorTypeIds.includes(value);
}
function isMeasurements(value) {
    if (!isRecord(value)) {
        return false;
    }
    if (value.mode === 'diy') {
        return (isFiniteNumber(value.height) &&
            isFiniteNumber(value.heightRight) &&
            isFiniteNumber(value.widthTop) &&
            isFiniteNumber(value.widthMiddle) &&
            isFiniteNumber(value.widthBottom));
    }
    if (value.mode === 'trade') {
        return isFiniteNumber(value.width) && isFiniteNumber(value.height);
    }
    return false;
}
function isSnapshot(value) {
    if (!isRecord(value)) {
        return false;
    }
    return (typeof value.sku === 'string' &&
        value.sku.length > 0 &&
        typeof value.description === 'string' &&
        isFiniteNumber(value.unitPrice) &&
        typeof value.colourName === 'string' &&
        typeof value.colourHex === 'string' &&
        typeof value.imageId === 'string' &&
        typeof value.bunningsItemNumber === 'string' &&
        isFiniteNumber(value.lockHeightMin) &&
        isFiniteNumber(value.lockHeightMax) &&
        typeof value.catalogueSyncedAt === 'string');
}
function isDoorConfig(value) {
    if (!isRecord(value)) {
        return false;
    }
    const approval = value.drawingApproval;
    return (typeof value.lineId === 'string' &&
        value.lineId.length > 0 &&
        value.lineId.length <= 40 &&
        typeof value.doorName === 'string' &&
        isDoorType(value.doorType) &&
        isMeasurements(value.measurements) &&
        typeof value.outOfSquareAcknowledged === 'boolean' &&
        (value.heightSpreadAcknowledged === undefined ||
            typeof value.heightSpreadAcknowledged === 'boolean') &&
        (value.depth === undefined || isFiniteNumber(value.depth)) &&
        (value.depthClearanceAcknowledged === undefined ||
            typeof value.depthClearanceAcknowledged === 'boolean') &&
        (approval === undefined ||
            (isRecord(approval) &&
                Number.isSafeInteger(approval.declarationVersion) &&
                typeof approval.declarationText === 'string' &&
                typeof approval.acceptedAt === 'string' &&
                typeof approval.binding === 'string')) &&
        isFiniteNumber(value.lockHeight) &&
        (value.lockSide === 'left' || value.lockSide === 'right') &&
        typeof value.colourCode === 'string' &&
        value.colourCode.length > 0 &&
        typeof value.quantity === 'number' &&
        Number.isInteger(value.quantity) &&
        value.quantity >= 1 &&
        isSnapshot(value.snapshot));
}
function isOptionalString(value) {
    return value === undefined || typeof value === 'string';
}
function asDoorsDesign(value) {
    if (!isRecord(value)) {
        return undefined;
    }
    if (value.schema !== 'doors' || value.version !== doorRules.designVersion) {
        return undefined;
    }
    if (!Array.isArray(value.doors) || !value.doors.every(isDoorConfig)) {
        return undefined;
    }
    if (!isOptionalString(value.termsAcceptedAt) ||
        !isOptionalString(value.clonedFrom) ||
        !isOptionalString(value.appVersion)) {
        return undefined;
    }
    return value;
}
/** `undefined` for anything that is not a doors design version 1. */
export function parseDoorsDesign(value) {
    if (typeof value === 'string') {
        try {
            return asDoorsDesign(JSON.parse(value));
        }
        catch {
            return undefined;
        }
    }
    return asDoorsDesign(value);
}
const doorOptionKeys = [
    'doorName',
    'doorType',
    'measurementMode',
    'mfgWidth',
    'mfgHeight',
    'widthTop',
    'widthMiddle',
    'widthBottom',
    'width',
    'height',
    'heightRight',
    'outOfSquareAck',
    'lockSide',
    'lockHeight',
    'lockTop',
    'lockBottom',
    'colourCode',
    'colourName',
    'bunningsItem',
    'catalogueSyncedAt',
];
const doorMarkerOptions = [
    'doorType',
    'measurementMode',
    'mfgWidth',
    'mfgHeight',
    'lockHeight',
];
function lineMatchesDoor(door, line, size) {
    const expected = Object.fromEntries(optionEntries(door, size));
    const actual = line.options ?? {};
    return (line.sku === sanitizeInput(door.snapshot.sku, door.snapshot.sku.length) &&
        line.quantity === door.quantity &&
        line.unitPrice === door.snapshot.unitPrice &&
        doorOptionKeys.every((key) => actual[key] === expected[key]));
}
function frozenSize(line) {
    const width = line.options?.mfgWidth;
    const height = line.options?.mfgHeight;
    if (!width || !height || !/^[1-9]\d*$/.test(width) || !/^[1-9]\d*$/.test(height)) {
        return undefined;
    }
    const size = { width: Number(width), height: Number(height) };
    return Number.isSafeInteger(size.width) && Number.isSafeInteger(size.height) ? size : undefined;
}
export function sameDoorMeasurements(first, second) {
    if (first.mode === 'trade' && second.mode === 'trade') {
        return first.width === second.width && first.height === second.height;
    }
    if (first.mode === 'diy' && second.mode === 'diy') {
        return (first.widthTop === second.widthTop &&
            first.widthMiddle === second.widthMiddle &&
            first.widthBottom === second.widthBottom &&
            first.height === second.height &&
            first.heightRight === second.heightRight);
    }
    return false;
}
/** Detect when retaining old measurements would change their frozen finished size. */
export function historicalSizeChange(previousDoor, previousItem, reviewedDoor) {
    if (!sameDoorMeasurements(previousDoor.measurements, reviewedDoor.measurements)) {
        return undefined;
    }
    const previous = frozenSize(previousItem);
    const reviewed = deriveManufacturedSize(reviewedDoor.measurements);
    if (!previous ||
        !reviewed ||
        (previous.width === reviewed.width && previous.height === reviewed.height)) {
        return undefined;
    }
    return { previous, reviewed };
}
/** Associate stored lines using server-recorded IDs or an unambiguous historical match. */
export function matchStoredDoorLines(design, items, lineIds) {
    if (design.doors.length !== items.length ||
        new Set(design.doors.map((door) => door.lineId)).size !== design.doors.length) {
        return undefined;
    }
    const byId = new Map(design.doors.map((door) => [door.lineId, door]));
    if (lineIds) {
        if (lineIds.length !== items.length || new Set(lineIds).size !== lineIds.length) {
            return undefined;
        }
        for (const [index, id] of lineIds.entries()) {
            const door = byId.get(id);
            const line = items[index];
            const size = line && frozenSize(line);
            if (!door || !line || !size || !lineMatchesDoor(door, line, size)) {
                return undefined;
            }
        }
        return [...lineIds];
    }
    const matchedIds = new Array(items.length);
    const used = new Set();
    for (const door of design.doors) {
        const candidates = items.flatMap((line, index) => {
            const size = frozenSize(line);
            return size && lineMatchesDoor(door, line, size) ? [index] : [];
        });
        if (candidates.length !== 1 || used.has(candidates[0])) {
            return undefined;
        }
        const index = candidates[0];
        used.add(index);
        matchedIds[index] = door.lineId;
    }
    return matchedIds;
}
/**
 * Validate a marked door design and its lines. `stored` is for unchanged
 * persisted orders only; its line IDs must come from the server, not a caller.
 */
export function validateDoorsOrder(design, items, options = {}) {
    const marked = isRecord(design) && design.schema === 'doors';
    if (!marked) {
        if (isRecord(design) && typeof design.schema === 'string' && design.schema.length > 0) {
            return { kind: 'not-doors' };
        }
        const hasDoorOptions = items.some((item) => doorMarkerOptions.every((key) => Object.hasOwn(item.options ?? {}, key)));
        return hasDoorOptions
            ? { kind: 'invalid', message: 'Door order lines require a matching door design' }
            : { kind: 'not-doors' };
    }
    const parsed = parseDoorsDesign(design);
    if (!parsed) {
        return { kind: 'invalid', message: 'The door design is malformed or unsupported' };
    }
    if (parsed.doors.length !== items.length || (!options.allowEmpty && items.length === 0)) {
        return { kind: 'invalid', message: 'The door design and order lines must match' };
    }
    if (new Set(parsed.doors.map((door) => door.lineId)).size !== parsed.doors.length) {
        return { kind: 'invalid', message: 'Door line identifiers must be unique' };
    }
    const mismatch = {
        kind: 'invalid',
        message: 'Door lines do not match the saved measurements and handle choices',
    };
    if (options.mode !== 'stored') {
        for (const [index, door] of parsed.doors.entries()) {
            const validation = validateDoorForCommit(door);
            if (!validation.valid) {
                return { kind: 'invalid', message: validation.message };
            }
            const line = items[index];
            if (!line || !lineMatchesDoor(door, line, validation.size)) {
                return mismatch;
            }
        }
        return { kind: 'valid', lineIds: parsed.doors.map((door) => door.lineId) };
    }
    const lineIds = matchStoredDoorLines(parsed, items, options.lineIds);
    if (!lineIds) {
        return mismatch;
    }
    const byId = new Map(parsed.doors.map((door) => [door.lineId, door]));
    for (const [index, id] of lineIds.entries()) {
        const door = byId.get(id);
        const line = items[index];
        const size = line && frozenSize(line);
        if (!door ||
            !size ||
            evaluateLockFit(door.lockHeight, door.doorType, size.height).status !== 'valid') {
            return mismatch;
        }
    }
    return { kind: 'valid', lineIds };
}
function displayOption(key, value) {
    if (key === 'doorType' && isDoorType(value)) {
        return doorCopy[value].title;
    }
    if (key === 'measurementMode') {
        if (value === 'diy') {
            return 'DIY';
        }
        if (value === 'trade') {
            return 'Trade';
        }
    }
    if (key === 'outOfSquareAck') {
        if (value === 'true') {
            return 'Yes';
        }
        if (value === 'false') {
            return 'No';
        }
    }
    if (key === 'lockSide') {
        if (value === 'left') {
            return 'Left';
        }
        if (value === 'right') {
            return 'Right';
        }
    }
    if (MM_OPTION_KEYS.has(key)) {
        return `${value} mm`;
    }
    return value;
}
/** Stable label/value rows for cart, email and admin. Missing keys are skipped. */
export function describeDoorOptions(options) {
    if (!options) {
        return [];
    }
    return OPTION_FIELDS.flatMap((field) => {
        const value = options[field.key];
        if (value === undefined) {
            return [];
        }
        return [{ label: field.label, value: displayOption(field.key, value) }];
    });
}
//# sourceMappingURL=serialize.js.map